import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, AlertCircle, RefreshCw, Copy } from 'lucide-react';
import { API } from './_helpers';

export default function PublicScheduleConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roster/public-config`, { withCredentials: true })
      .then(r => setCfg(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Could not load'));
  }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(cfg.url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt('Copy this URL:', cfg.url); }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the public URL? Anyone using the old link will no longer be able to view the schedule.')) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/roster/public-config/rotate`, null, { withCredentials: true }); setCfg({ ...cfg, ...r.data }); }
    catch (e) { alert(e?.response?.data?.detail || 'Rotate failed'); }
    setBusy(false);
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/roster/public-config/toggle`, { enabled: !cfg.enabled }, { withCredentials: true });
      setCfg({ ...cfg, enabled: r.data.enabled });
    } catch (e) { alert('Could not toggle'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}
           data-testid="public-cfg-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#0d2644' }}>Public schedule URL</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              A read-only, no-login view of the team week roster. Stick a QR on the staff-room notice board, or paste in WhatsApp.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        {!cfg && !err && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>}

        {cfg && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <code className="flex-1 text-xs bg-muted/50 px-2.5 py-2 rounded border break-all" data-testid="public-url">{cfg.url}</code>
              <button onClick={copy} className="text-xs font-semibold px-3 py-2 rounded-lg border whitespace-nowrap inline-flex items-center gap-1"
                      data-testid="public-copy">
                <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy'}
              </button>
              <a href={cfg.url} target="_blank" rel="noopener noreferrer"
                 className="text-xs font-semibold px-3 py-2 rounded-lg bg-[#0d6efd] text-white whitespace-nowrap"
                 data-testid="public-open">Open</a>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${cfg.enabled ? 'bg-green-500' : 'bg-slate-400'}`} />
                {cfg.enabled ? 'Enabled — link is live' : 'Disabled — link returns 404'}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={toggle} disabled={busy} data-testid="public-toggle"
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border">
                  {cfg.enabled ? 'Disable' : 'Enable'}
                </button>
                <button onClick={rotate} disabled={busy} data-testid="public-rotate"
                        className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-amber-700 border-amber-300 hover:bg-amber-50">
                  <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Rotate URL
                </button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground flex items-start gap-2 border-t pt-3">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                The public view shows staff names, role, time and site colour — but <strong>no email addresses or phone numbers</strong>. Treat the URL like a password; anyone with the link can see who&apos;s rostered.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
